import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';

import { TP_COLORS } from '../../../../../constants';
import {
  ISectionForAdmin,
  deleteCourseResourceSectionByIdAsAdmin,
  getSectionsState,
} from '../../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/UserSlice';
import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';

interface ResourceSectionItemProps {
  orderNumber: number;
  section: ISectionForAdmin;
  onEdit: (section: ISectionForAdmin) => void;
}

const viewResourceButtonStyles: SxProps = {
  backgroundColor: '#3498DB',
  color: TP_COLORS.white,
  p: '8px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const containerStyles: SxProps = {
  backgroundColor: 'transparent',
  borderRadius: '10px',
  border: '1px dashed #CBD5E1',
};

const ResourceSectionItem: React.FC<ResourceSectionItemProps> = ({
  orderNumber,
  section,
  onEdit,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const sectionsState = useAppSelector(getSectionsState);

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this resource?',
    }).then(() => {
      dispatch(deleteCourseResourceSectionByIdAsAdmin(section._id));
    });
  };

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent sx={{ p: '16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
            <Avatar
              sx={{
                width: '36px',
                height: '36px',
                backgroundColor: '#1C9FF7',
                mr: '15px',
              }}
            >
              {orderNumber}
            </Avatar>
            <Stack gap={1}>
              <Chip
                size="small"
                label="Resource"
                color="info"
                sx={{ width: 'fit-content' }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                className="ellipsis-text one-line"
              >
                {section.curriculum} - {section.quiztopiyaTopicName} -{' '}
                {section.quiztopiyaSubTopicName}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            {section.quiztopiyaResourceUrl ? (
              <ButtonBase
                href={`${process.env.PUBLIC_URL}/${section.quiztopiyaResourceUrl}`}
                target="_blank"
                sx={viewResourceButtonStyles}
              >
                View Resource
              </ButtonBase>
            ) : null}
            {checkUserPermission(authState.user, {
              adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
            }) ? (
              <>
                <Tooltip title="Delete">
                  <IconButton
                    color="error"
                    onClick={handleDelete}
                    disabled={sectionsState.status === 'loading'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() => onEdit(section)}
                    disabled={sectionsState.status === 'loading'}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ResourceSectionItem;
